import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  FormFeedback,
  Button, // Import FormFeedback for error messages
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import * as Yup from "yup"; // Import Yup for validation
import MediaService from "../../../services/KangarooServices/MediaServices";
import { useFormik } from "formik";
import { CKEditor } from "@ckeditor/ckeditor5-react";
// import { useQuill } from "react-quilljs"; // This was unused
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import PageService from "../../../services/KangarooServices/PageService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
// import FileUpload from "../../Media/component/FileUpload"; // This was unused
import MediaModal from "../Component/MediaModal";
import { imageBaseApi } from "../../../utilis/imageHelper";
import Select from "react-select";

const PageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detailData, setDetailData] = useState({});
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);
  const [slugInput, setSlugInput] = useState("");
  const [slugMessage, setSlugMessage] = useState(""); // message text
  const [isSlugValid, setIsSlugValid] = useState(true); // validity

  /* ---------------- HELPERS ---------------- */
  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-");

  const checkSlug = async (slug) => {
    if (!slug) return;
    try {
      const res = await PageService.checkSlug({ slug });
      setIsSlugValid(!res.exists);
      setSlugMessage(
        res.exists
          ? `Slug taken. Suggested: ${res.suggestedSlug}`
          : "Slug available"
      );
    } catch {
      setIsSlugValid(false);
      setSlugMessage("Unable to check slug");
    }
  };

  // Fetch page detail
  const fetchPageDetail = async () => {
    try {
      const response = await PageService.getBySlug(id);
      setDetailData(response);
      console.log(detailData);
      // if (response.featuredMediaId) {
      //   const media = response.mediaItems?.find(
      //     (m) => m.id === response.featuredMediaId
      //   );
      //   console.log(media)
      //   if (media) setSelectedMedia(media);
      // }
      setSelectedMedia(response.featuredMedia);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) fetchPageDetail();
  }, [id]);

  // Define Validation Schema
  const validationSchema = Yup.object({
    title: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .max(255, "Title must not exceed 255 characters"),
    subtitle: Yup.string().max(500, "Subtitle must not exceed 500 characters"),
    content: Yup.string()
      .required("Content is required")
      .min(10, "Content must be at least 10 characters"),
    metaTitle: Yup.string()
      .required("Meta Title is required")
      .max(60, "Meta title should not exceed 60 characters"),
    metaDescription: Yup.string()
      .required("Meta Description is required")
      .max(160, "Meta description should not exceed 160 characters"),

    // Note: featuredMediaId is handled outside of direct form inputs.
  });
  const STATUS_MAP = {
    Published: 1,
    Draft: 2,
  };

  const STATUS_REVERSE_MAP = {
    1: "Published",
    2: "Draft",
  };

  const formik = useFormik({
    initialValues: {
      id: detailData?.id || "",
      slug: detailData?.slug || "",
      pageType: detailData?.pageType || "",
      title: detailData?.title || "",
      subtitle: detailData?.subtitle || "",
      content: detailData?.content || "",
      metaTitle: detailData?.metaTitle || "",
      metaDescription: detailData?.metaDescription || "",
      featuredMediaId: detailData?.featuredMediaId || "",
      isPdf: detailData?.isPdf || false,
      status: detailData?.status || "Published",
    },
    enableReinitialize: true,
    validationSchema: validationSchema, // Apply the validation schema

    onSubmit: async (values) => {
      // 💡 FIX: Manually check for errors and set all fields as touched
      // to display errors for fields that were never focused/blurred.
      await formik.validateForm();

      if (Object.keys(formik.errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.initialValues).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          false
        );
        return; // Stop submission if there are validation errors
      }

      try {
        // Build payload
        const payload = {
          slug: values?.slug,
          title: values.title,
          subtitle: values.subtitle,
          content: values.content,
          featuredMediaId: selectedMedia?.id || null,
          metaTitle: values.metaTitle,
          metaDescription: values.metaDescription,
          status: STATUS_MAP[values.status],
          pageType: values?.pageType,
          isPdf: values?.isPdf || false,
        };

        if (values?.id) {
          await PageService.update(values?.id, payload);
          toast.success("Page updated successfully");
        } else {
          await PageService.create(payload);
          toast.success("Page created successfully");
        }

        navigate(`/pages-list`);
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong!");
      }
    },
  });

  const breadcrumbItems = [{ title: "Back to List", link: "/pages-list" }];
  const PageTypeEnum = {
    Blogs: 1,
    Events: 2,
    Services: 3,
    GeneralPages: 4,
    Home: 5,

    AboutUs: 6,
    InstitusionWeRepresent: 7,
    WhoWeAre: 8,
    PartnerShips: 9,
    Ceo: 10,
    Careers: 11,
    BecomeOurFranchise: 12,
    ContactUs: 13,
  };
  const pageTypeOptions = Object.entries(PageTypeEnum).map(
    ([label, value]) => ({
      label,
      value,
    })
  );

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Page Form"
          pageTitle="Page"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button type="submit" className="btn btn-success">
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/pages-list")}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Title Field */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="title">
                      Title <span className="text-danger">*</span>
                    </Label>
                    {/* <Input
                      id="title"
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.title && !!formik.errors.title}
                    /> */}
                    <Input
                      value={formik.values.title}
                      onChange={(e) => {
                        formik.setFieldValue("title", e.target.value);

                        const slug = generateSlug(e.target.value);
                        formik.setFieldValue("slug", slug);
                        checkSlug(slug);
                      }}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                    )}
                  </FormGroup>
                  <FormGroup className="mb-3">
                    <Label htmlFor="slug">
                      Slug <span className="text-danger">*</span>
                    </Label>
                    <Input
                      invalid={!isSlugValid}
                      value={formik.values.slug}
                      onBlur={(e) => checkSlug(e.target.value)}
                      onChange={(e) =>
                        formik.setFieldValue("slug", e.target.value)
                      }
                    />
                    <small
                      className={isSlugValid ? "text-success" : "text-danger"}
                    >
                      {slugMessage}
                    </small>
                  </FormGroup>

                  {/* Subtitle Field */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="subtitle">Sub Title</Label>
                    <Input
                      id="subtitle"
                      name="subtitle"
                      value={formik.values.subtitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.subtitle && !!formik.errors.subtitle
                      }
                    />
                    {formik.touched.subtitle && formik.errors.subtitle && (
                      <FormFeedback>{formik.errors.subtitle}</FormFeedback>
                    )}
                  </FormGroup>

                  {/* Content Field (CKEditor) */}
                  <FormGroup className="mb-3">
                    <Label>
                      Content <span className="text-danger">*</span>
                    </Label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={formik.values.content}
                      onChange={(event, editor) =>
                        formik.setFieldValue("content", editor.getData())
                      }
                      onBlur={() => formik.setFieldTouched("content", true)} // Manual touch for validation
                    />
                    {/* Custom error display for CKEditor content */}
                    {formik.touched.content && formik.errors.content && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "0.875em" }}
                      >
                        {formik.errors.content}
                      </div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <CardBody>
                  <Label style={{ cursor: "pointer" }}>Featured Media</Label>
                  {selectedMedia ? (
                    <div className="text-center p-3 border rounded shadow-sm bg-light">
                      {/* Image Preview */}
                      <div
                        style={{
                          maxHeight: "220px",
                          overflow: "hidden",
                          borderRadius: "10px",
                          border: "1px solid #ddd",
                        }}
                        className="mb-3"
                      >
                        <img
                          src={`${imageBaseApi}${selectedMedia?.url}`}
                          alt="Featured"
                          style={{ width: "100%", objectFit: "cover" }}
                          className="img-fluid hover-zoom"
                        />
                      </div>

                      {/* Image Info */}
                      <div className="mb-2">
                        <div className="fw-semibold">
                          {selectedMedia.name || "Featured Image"}
                        </div>

                        {selectedMedia.size && (
                          <small className="text-muted">
                            Size: {(selectedMedia.size / 1024).toFixed(1)} KB
                          </small>
                        )}
                      </div>

                      {/* Buttons */}
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          className="rounded-pill px-3"
                          onClick={toggleMedia}
                        >
                          Change
                        </Button>

                        <Button
                          size="sm"
                          outline
                          color="danger"
                          className="rounded-pill px-3"
                          onClick={() => {
                            // formik.setFieldValue("featuredMediaId", "");
                            setSelectedMedia(null);
                            formik.setFieldValue("featuredMedia", null);
                          }}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded text-center bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-2 text-muted">
                        No image selected
                      </h6>
                      <h6 className="mb-3 text-muted">1200 x 630</h6>
                      <Button
                        outline
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={toggleMedia}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose
                        Featured Image
                      </Button>
                    </div>
                  )}
                  {/* {selectedMedia ? (
                    <div className="mt-2 text-center">
                      <img
                        src={`${imageBaseApi}${selectedMedia?.url}`}
                        alt={selectedMedia.fileName}
                        style={{
                          maxHeight: "200px",
                          maxWidth: "100%",
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted mt-2">No media selected</p>
                  )} */}
                </CardBody>
              </Card>

              <Card className="mt-3">
                <CardHeader>Meta Data</CardHeader>
                <CardBody>
                  {/* Meta Title Field */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="metaTitle">
                      Meta Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="metaTitle"
                      name="metaTitle"
                      value={formik.values.metaTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.metaTitle && !!formik.errors.metaTitle
                      }
                    />
                    {formik.touched.metaTitle && formik.errors.metaTitle && (
                      <FormFeedback>{formik.errors.metaTitle}</FormFeedback>
                    )}
                  </FormGroup>

                  {/* Meta Description Field */}
                  <FormGroup className="mb-3">
                    <Label htmlFor="metaDescription">
                      Meta Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="metaDescription"
                      name="metaDescription"
                      type="textarea"
                      rows={3}
                      value={formik.values.metaDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.metaDescription &&
                        !!formik.errors.metaDescription
                      }
                    />
                    {formik.touched.metaDescription &&
                      formik.errors.metaDescription && (
                        <FormFeedback>
                          {formik.errors.metaDescription}
                        </FormFeedback>
                      )}
                  </FormGroup>

                  {/* Status Field */}
                  <FormGroup className="d-flex justify-content-between">
                    <Label>Is Pdf?</Label>
                    <Input
                      type="checkbox"
                      checked={formik.values.isPdf}
                      onChange={(e) =>
                        formik.setFieldValue("isPdf", e.target.checked)
                      }
                    />
                  </FormGroup>
                  <div className="mb-3">
                    <Label>Status</Label>
                    <select
                      name="status"
                      className="form-select"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                    >
                      <option value="Published">Published</option>
                      <option value="Draft">Draft</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <Label>Page Type</Label>
                    <Select
                      options={pageTypeOptions}
                      name="pageType"
                      value={
                        pageTypeOptions.find(
                          (option) => option.value === formik.values.pageType
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        formik.setFieldValue(
                          "pageType",
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      placeholder="Select Page Type"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </div>

      <MediaModal
        isOpen={isMediaOpen}
        toggle={toggleMedia}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
      />
    </Container>
  );
};

export default PageForm;
